import type { GameSnapshot, SceneNode } from '@aod/protocol';
// Selection body for the right panel; routes the active hex entity without a shell wrapper.
import type { CitySuggestion } from '@aod/sim-adapter';
import { useMemo, useState } from 'react';
import {
  type HeroQuestKind,
  type HeroQuests,
  heroIdOfUnit,
  huntTargets as huntTargetsOf,
  questOfHero,
} from '../../heroes/quests.js';
import { useT } from '../../i18n/index.js';
import type { Archetype } from '../DoctrinePanel/archetypes.js';
import type { BuildingDef } from '../InfoPanel/BuildingSection.js';
import { CityTabContent } from '../InfoPanel/CityTabContent.js';
import { CombatPreviewPanel } from '../InfoPanel/CombatPreviewPanel.js';
import { TileTabContent } from '../InfoPanel/TileTabContent.js';
import { UnitTabContent } from '../InfoPanel/UnitTabContent.js';
import { EntityModal } from '../Modal/EntityModal.js';
import { type SubTabPick, resolveSubTab } from './tabFollow.js';

interface SelectionTabProps {
  readonly selected: SceneNode;
  readonly snapshot: { scene: { children?: SceneNode[] } };
  readonly localPlayerIndex: number;
  readonly matchOver: boolean;
  readonly garrisonCount: number;
  readonly buildings: ReadonlyArray<BuildingDef>;
  readonly treasuryIndustry: number;
  readonly onAttack: (cityId: string) => void;
  readonly onDeploy: (cityId: string) => void;
  readonly onStartBuilding: (cityId: string, buildingId: string) => void;
  /** Take the offer a foreign house has made to a harbour. */
  readonly onAcceptMerchant: (cityId: string) => void;
  /** Refuse it. */
  readonly onDeclineMerchant: (cityId: string) => void;
  readonly onSetDoctrine: (cityId: string, archetype: Archetype) => void;
  readonly suggestions: readonly CitySuggestion[];
  readonly onApplySuggestion: (idea: CitySuggestion) => void;
  readonly onActivateAbility: (unitId: string, slotIndex: number) => void;
  readonly onSelectAbility: (unitId: string, slotIndex: number, abilityId: string) => void;
  readonly onSetFormation: (unitId: string, formation: string) => void;
  /** The orders the local house's heroes carry, while `heroes.quests` is on. */
  readonly heroQuests?: HeroQuests | null;
  /** Give a hero an order. */
  readonly onDispatchHeroQuest?: (
    unitId: string,
    kind: HeroQuestKind,
    amount?: number,
    targetUnitId?: string,
  ) => boolean;
  /** Phone sheet: show the tab matching the selection (and reset when it changes). */
  readonly followSelection?: boolean;
}

interface TabDef {
  key: string;
  label: string;
  badge?: string;
}

export function SelectionTab(props: SelectionTabProps) {
  const { selected, snapshot } = props;
  const t = useT();
  const allNodes = snapshot.scene.children ?? [];
  // The enemy heroes a hunt may name — they are on the board, so the client
  // can see them.
  const huntTargets = useMemo(
    () => huntTargetsOf(allNodes, props.localPlayerIndex),
    [allNodes, props.localPlayerIndex],
  );
  const sq = selected.props?.q as number | undefined;
  const sr = selected.props?.r as number | undefined;
  const atPosition =
    sq !== undefined && sr !== undefined
      ? allNodes.filter(
          (node) => node.props?.q === sq && node.props?.r === sr && node.type !== 'tile',
        )
      : [];
  const unitNodes = atPosition.filter((node) => node.type === 'unit');
  const cityNode = atPosition.find((node) => node.type === 'city');
  const tileNode =
    sq !== undefined && sr !== undefined
      ? allNodes.find(
          (node) => node.type === 'tile' && node.props?.q === sq && node.props?.r === sr,
        )
      : null;

  const tabs: TabDef[] = [];
  if (unitNodes.length > 0) {
    tabs.push({
      key: 'unit',
      label:
        unitNodes.length > 1
          ? t('selection.units', { count: unitNodes.length })
          : t('selection.unit'),
      badge: unitNodes.length > 1 ? String(unitNodes.length) : undefined,
    });
  }
  if (cityNode) tabs.push({ key: 'city', label: t('selection.city') });
  if (tileNode) tabs.push({ key: 'tile', label: t('selection.tile') });

  const fallback = tabs[0]?.key ?? 'tile';
  const [activeTab, setActiveTab] = useState(fallback);
  const [pick, setPick] = useState<SubTabPick | null>(null);
  const resolvedActive = resolveSubTab({
    available: tabs.map((tab) => tab.key),
    selectedType: selected.type,
    selectionId: selected.id,
    follow: props.followSelection === true,
    current: activeTab,
    pick,
  });
  const [modalOpen, setModalOpen] = useState(false);

  // The modal follows the active tab so its actions target the entity being inspected.
  const modalNode = (() => {
    if (resolvedActive === 'unit' && unitNodes[0]) return unitNodes[0];
    if (resolvedActive === 'city' && cityNode) return cityNode;
    if (resolvedActive === 'tile' && tileNode) return tileNode;
    return null;
  })();

  return (
    <div className="flex flex-col gap-3">
      {tabs.length > 1 && (
        <div className="flex gap-0.5 rounded-lg bg-muted/40 p-0.5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`flex-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                resolvedActive === tab.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => {
                setActiveTab(tab.key);
                setPick({ tab: tab.key, forSelectionId: selected.id });
              }}
            >
              {tab.label}
              {tab.badge && (
                <span className="ml-1 rounded-full bg-primary/20 px-1 py-0 text-[9px]">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {resolvedActive === 'unit' &&
        unitNodes.map((unit) => (
          <UnitTabContent
            key={unit.id}
            unit={unit}
            localPlayerIndex={props.localPlayerIndex}
            onActivateAbility={props.onActivateAbility}
            onSelectAbility={props.onSelectAbility}
            onSetFormation={props.onSetFormation}
            heroQuest={questOfHero(props.heroQuests ?? null, heroIdOfUnit(unit.id) ?? -1)}
            huntTargets={huntTargets}
            onDispatchHeroQuest={props.onDispatchHeroQuest}
          />
        ))}
      {resolvedActive === 'city' && cityNode && (
        <CityTabContent
          city={cityNode}
          localPlayerIndex={props.localPlayerIndex}
          matchOver={props.matchOver}
          garrisonCount={props.garrisonCount}
          buildings={props.buildings}
          treasuryIndustry={props.treasuryIndustry}
          onAttack={props.onAttack}
          onDeploy={props.onDeploy}
          onStartBuilding={props.onStartBuilding}
          onSetDoctrine={props.onSetDoctrine}
          onAcceptMerchant={props.onAcceptMerchant}
          onDeclineMerchant={props.onDeclineMerchant}
          suggestions={props.suggestions}
          onApplySuggestion={props.onApplySuggestion}
        />
      )}
      {resolvedActive === 'tile' && tileNode && <TileTabContent tile={tileNode} />}

      {resolvedActive === 'unit' &&
        unitNodes
          .filter((unit) => (unit.props?.ownerId as number | undefined) === props.localPlayerIndex)
          .map((unit) => (
            <CombatPreviewPanel
              key={`preview-${unit.id}`}
              attacker={unit}
              snapshot={snapshot as GameSnapshot}
              localPlayerIndex={props.localPlayerIndex}
            />
          ))}

      {modalOpen && modalNode && (
        <EntityModal
          node={modalNode}
          localPlayerIndex={props.localPlayerIndex}
          matchOver={props.matchOver}
          onClose={() => setModalOpen(false)}
          onAttack={props.onAttack}
          onDeploy={props.onDeploy}
        />
      )}
    </div>
  );
}
